import type { MultiselectFilterType, SelectFilterType } from './types';
import { defaultFilterOptions, defaultFilterSelection } from './types';
import { usePricesStore } from '@/infrastructure/prices/usePricesStore';
import { partial } from 'ramda';
import { useAccommodationContext } from '@/react-pages/Prices/AccommodationContext';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useDependencies } from '@/scopes/common/infrastructure/dependencies';
import { useAccommodationAvailability } from '@/domain/prices/availability/hooks';
import { type MappedFiltersResponse } from '@/infrastructure/prices/filters/api/filtersResponseMapper';

export const useFiltersQuery = () => {
  const { filtersAPIClient } = useDependencies();
  const { isAccommodationAvailable } = useAccommodationAvailability();
  const accommodationContext = useAccommodationContext();
  const { filterSelection, setFiltersSelection } = usePricesStore();

  const { isLoading, isFetching, data } = useQuery({
    queryKey: ['filters-selection', JSON.stringify(filterSelection?.Participants)],
    queryFn: async () => {
      try {
        const filtersResult = await filtersAPIClient.getFilters({
          ...accommodationContext,
          filterSelection,
        });
        if (!filtersResult.isEmpty) {
          setFiltersSelection(filtersResult.filterSelection);
        }
        return filtersResult;
      } catch (e) {
        return {
          filterSelection: defaultFilterSelection,
          ...defaultFilterOptions,
          isError: true,
          isEmpty: false,
        };
      }
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    retry: false,
    enabled: isAccommodationAvailable,
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const filtersResult = await filtersAPIClient.getFilters({
        ...accommodationContext,
        filterSelection: defaultFilterSelection,
      });
      if (filtersResult !== null) {
        setFiltersSelection(filtersResult.filterSelection);
      }
    },
  });

  return {
    data,
    isLoading,
    isFetching,
    isError: data?.isError,
    isEmptyResponse: data?.isEmpty ?? false,
    filterOptions: data ?? defaultFilterOptions,
    filterSelection: { ...defaultFilterSelection, ...data?.filterSelection, ...filterSelection },
    resetSelection: resetMutation.mutate,
  };
};

export function usePriceFilters() {
  const { resetSelection, isEmptyResponse, isError, isLoading, data } = useFiltersQuery();

  return {
    data,
    isEmptyResponse,
    resetSelection,
    isError,
    isLoading,
  };
}

export function useSelectFilter<K extends SelectFilterType>(filterType: K) {
  const { filterOptions, filterSelection } = useFiltersQuery();
  const { setFilterSelection } = usePricesStore();

  return {
    options: filterOptions[filterType].options,
    selectedValue: filterSelection[filterType] || '',
    setSelection: partial(setFilterSelection, [filterType]),
  };
}

export function useMultiSelectFilter<K extends MultiselectFilterType>(filterType: K) {
  const { filterOptions, filterSelection } = useFiltersQuery();
  const { setFilterSelection } = usePricesStore();

  return {
    options: filterOptions[filterType].options,
    selectedValues: filterSelection[filterType] || [],
    setSelection: partial(setFilterSelection, [filterType]),
  };
}

export function useMonthFilter() {
  const { filterOptions, filterSelection } = useFiltersQuery();
  const { setFilterSelection } = usePricesStore();

  const isFirstMonth =
    filterSelection.Month !== undefined && filterSelection.Month === filterOptions.Month.options[0]?.value;
  const isLastMonth =
    filterSelection.Month !== undefined &&
    filterSelection.Month === filterOptions.Month.options[filterOptions.Month.options.length - 1]?.value;

  const goToNextMonth = () => {
    if (filterSelection.Month === undefined) return;
    const nextMonthIndex = filterOptions.Month.options.findIndex((month) => month.value === filterSelection.Month) + 1;
    setFilterSelection('Month', filterOptions.Month.options[nextMonthIndex]?.value);
  };

  const goToPreviousMonth = () => {
    if (filterSelection.Month === undefined) return;
    const previousMonthIndex =
      filterOptions.Month.options.findIndex((month) => month.value === filterSelection.Month) - 1;
    setFilterSelection('Month', filterOptions.Month.options[previousMonthIndex]?.value);
  };

  return {
    options: filterOptions.Month.options,
    selectedValue: filterSelection.Month || '',
    setSelection: partial(setFilterSelection, ['Month']),
    isFirstMonth,
    isLastMonth,
    goToNextMonth,
    goToPreviousMonth,
  };
}
